import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const LandingHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 10) {
        setScrolled(false);
      } else {
        if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
          setScrolled(true);
        } else if (currentScrollY < lastScrollY.current - 10 && currentScrollY < 50) {
          setScrolled(false);
        }
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 py-4">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: 1,
          y: 0,
          maxWidth: scrolled ? "56rem" : "80rem",
          borderRadius: scrolled ? "9999px" : "0.75rem",
          backgroundColor: scrolled ? "rgba(3, 10, 7, 0.78)" : "transparent",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          boxShadow: scrolled
            ? "0 10px 25px -5px rgba(0, 0, 0, 0.35), 0 8px 10px -6px rgba(0, 0, 0, 0.25)"
            : "none",
          borderWidth: scrolled ? "1px" : "0px",
          borderColor: scrolled ? "rgba(255, 255, 255, 0.08)" : "transparent",
        }}
        transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1],
        }}
        className={`w-full ${scrolled ? "px-6 sm:px-8 py-3" : "px-6 sm:px-8 py-3"}`
        }
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://res.cloudinary.com/dmw1bwmpr/image/upload/v1745139360/Main_Icon_qxmxdv.svg"
                alt="amPortal"
                className={`${scrolled ? "w-8 h-8" : "w-10 h-10"} transition-all duration-300`}
              />
              <span
                className={`${scrolled ? "text-lg sm:text-xl" : "text-lg sm:text-xl md:text-2xl"} text-white font-bold transition-all duration-300`}
              >
                amPortal
              </span>
            </Link>
          </div>

          <nav className={`hidden md:flex items-center ${scrolled ? "gap-6 mx-4" : "gap-6 lg:gap-8"}`}>
            <a href="#ampassport" className="text-white/80 hover:text-[#00ff9d] transition-colors whitespace-nowrap">
              amPassport
            </a>
            <a href="#amaia" className="text-white/80 hover:text-[#00ff9d] transition-colors whitespace-nowrap">
              AMAIA
            </a>
            <a href="#how-it-works" className="text-white/80 hover:text-[#00ff9d] transition-colors whitespace-nowrap">
              How It Works
            </a>
            <a href="#ecosystem" className="text-white/80 hover:text-[#00ff9d] transition-colors whitespace-nowrap">
              Ecosystem
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/amverify"
              className={`
                rounded-full border border-[#00ff9d]/40 text-[#00ff9d]
                hover:bg-[#00ff9d] hover:text-[#040606] transition-all font-medium 
                ${scrolled ? "px-4 py-2 text-sm" : "px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm md:text-base"}
              `}
            >
              Launch App
            </Link>

            <button
              ref={buttonRef}
              className={`
                md:hidden flex items-center justify-center rounded-full 
                bg-[#00ff9d]/10 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/30
                ${scrolled ? "w-8 h-8" : "w-8 h-8 sm:w-10 sm:h-10"}
              `}
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={scrolled ? "16" : "18"}
                height={scrolled ? "16" : "18"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#00ff9d]"
              >
                {mobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
            }}
            className="fixed top-24 left-4 right-4 z-40 md:hidden mx-auto max-w-xs"
          >
            <div className="bg-black/80 backdrop-blur-xl rounded-2xl p-4 shadow-xl border border-white/10">
              <nav className="flex flex-col space-y-2">
                <a
                  href="#ampassport"
                  className="text-white text-base font-medium hover:text-[#00ff9d] transition-colors px-4 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  amPassport
                </a>
                <a
                  href="#amaia"
                  className="text-white text-base font-medium hover:text-[#00ff9d] transition-colors px-4 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  AMAIA
                </a>
                <a
                  href="#how-it-works"
                  className="text-white text-base font-medium hover:text-[#00ff9d] transition-colors px-4 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  How It Works
                </a>
                <a
                  href="#ecosystem"
                  className="text-white text-base font-medium hover:text-[#00ff9d] transition-colors px-4 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Ecosystem
                </a>
                <Link
                  to="/amverify"
                  className="text-white text-base font-medium hover:text-[#00ff9d] transition-colors px-4 py-3 rounded-xl hover:bg-white/5 active:bg-white/10 flex items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Launch App
                </Link>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingHeader;
